import api from "./client";

// ---------- TIPOS ----------

export type DestinoAutomatico = "ESTOQUE" | "TRATATIVA";

/** Para qualidades de TRATATIVA: a quem a devolução se destina. */
export type TipoTratativa = "DEVOLUCAO_FORNECEDOR" | "DEVOLUCAO_CLIENTE";

export interface QualidadeProduto {
	id: number;
	nome: string;
	destinoAutomatico: DestinoAutomatico;
	/** Item desta qualidade entra na NF de devolução (Perfeito/Defeito = true). */
	entraNotaDevolucao: boolean;
	/** Tratativa (FORNECEDOR/CLIENTE) — só para destino TRATATIVA; null p/ ESTOQUE. */
	tipoTratativa?: TipoTratativa | null;
	ativo: boolean;
}

export interface MotivoDevolucao {
	id: number;
	nome: string;
	ativo: boolean;
}

export interface CadastrarQualidadeDto {
	nome: string;
	destinoAutomatico: DestinoAutomatico;
	/** Opcional; default true. Marque false para Usado/Outros (suprimem a NF). */
	entraNotaDevolucao?: boolean;
	/** Só p/ TRATATIVA: devolução ao FORNECEDOR ou ao CLIENTE. Default fornecedor. */
	tipoTratativa?: TipoTratativa;
}

export interface CadastrarMotivoDto {
	nome: string;
}

export type OrigemDevolucao = "CLIENTE" | "FORNECEDOR" | "MESA_SEPARACAO";

/**
 * Eixo do WMS. `PREVISTA` só existe em devolução importada de marketplace: o
 * canal registrou e o pacote ainda não chegou. As criadas no WMS nascem em
 * `RECEBIDA`, como sempre.
 *
 * Não confundir com `statusExterno`, que é o espelho cru do canal e inclui
 * disputa e mediação — são dois eixos independentes.
 */
export type StatusDevolucao =
	| "PREVISTA"
	| "RECEBIDA"
	| "EM_CONFERENCIA"
	| "CONFERIDA";

/**
 * Para onde o pacote vai, em vocabulário **nosso** — não do canal.
 *
 * `CD_PROPRIO` vem para a nossa doca; `CD_CANAL` fica no CD do marketplace e só
 * chega se a triagem reprovar. `null` = devolução nascida no WMS.
 *
 * A tradução do vocabulário do canal (`seller_address`/`warehouse` no ML) é
 * feita no backend, no adapter de cada canal.
 */
export type DestinoDevolucao = "CD_PROPRIO" | "CD_CANAL";

/**
 * `APROVADO` é o veredito da conferência, não o lançamento: o item passou na
 * avaliação e a qualidade escolhida manda ele para o estoque. A entrada física
 * só acontece depois, quando toda ocorrência da devolução é finalizada (#1871).
 */
export type StatusItemDevolucao =
	| "PENDENTE_AVALIACAO"
	| "APROVADO"
	| "AGUARDANDO_TRATATIVA";

export interface ConferirNotaItemPayload {
	produtoId: number;
	quantidade: number;
	qualidadeId: number;
	motivoId: number;
}

export interface ConferirNotaPayload {
	origem: OrigemDevolucao;
	notaFiscalId?: number;
	itens: ConferirNotaItemPayload[];
}

export interface ItemDevolucaoApi {
	devolucaoId?: number;
	produtoId: number;
	quantidade: number;
	status: StatusItemDevolucao;
	qualidadeProdutoId?: number;
	motivoDevolucaoId?: number;
}

export interface DevolucaoResumo {
	id: number;
	status: StatusDevolucao;
	origem: OrigemDevolucao;
	notaFiscalId?: number;
}

export interface DevolucaoDetalhe extends DevolucaoResumo {
	itens: ItemDevolucaoApi[];
}

/**
 * Onde está a emissão da NF de devolução (#1896).
 *
 * `NAO_APLICAVEL` é o caso comum e **não é pendência**: Mercado Livre não gera
 * nota por regra, qualidade que não entra na nota não gera, e a devolução que
 * chegou com nota de terceiro não tem o que emitir.
 */
export type StatusEmissaoNotaDevolucao =
	| "NAO_APLICAVEL"
	| "PENDENTE"
	| "ERRO"
	| "EMITIDA";

/**
 * Onde está o lançamento do estoque na Tiny (#2141): a NF entra no Geral e o
 * "Com defeito" é transferido para o Defeito.
 *
 * - `PARCIAL` — a NF entrou, alguma transferência não.
 * - `ERRO` — a NF não entrou; nada foi transferido.
 * - `PENDENTE` — começou e não registrou desfecho.
 */
export type StatusEstoqueTinyDevolucao =
	| "NAO_APLICAVEL"
	| "PENDENTE"
	| "CONCLUIDO"
	| "PARCIAL"
	| "ERRO";

/** Linha da listagem de devoluções (read model enriquecido com o canal). */
export interface DevolucaoListaItem {
	id: number;
	status: StatusDevolucao;
	origem: OrigemDevolucao;
	notaFiscalId?: number;
	pedidoId?: number;
	notaVendaId?: number;
	/**
	 * Canal de venda resolvido — pelo pedido, pela nota ou, em devolução de
	 * marketplace sem pedido, pela conta do canal. `null` = não resolvido.
	 */
	canalVenda: string | null;
	/** Número do pedido de origem; null = não resolvido. */
	numPedido: string | null;
	/** Nome do cliente (pedido ou destinatário/emitente da nota); null = não resolvido. */
	clienteNome: string | null;
	/**
	 * Número da NF de devolução; `null` enquanto a emissão não terminou.
	 *
	 * É o que a coluna mostra — o operador procura a nota pelo número, na Tiny
	 * e no DANFE, não pelo id interno.
	 */
	notaDevolucaoNumero: string | null;
	/** Onde está a emissão da NF de devolução (#1896). */
	statusEmissaoNota: StatusEmissaoNotaDevolucao;
	/** Causa da última falha de emissão; `null` quando não houve. */
	emissaoErro: string | null;
	/** Quantas vezes a emissão foi tentada. */
	emissaoTentativas: number;
	/**
	 * A insistência automática se esgotou — a "falha permanente" da decisão da
	 * Ana. O botão de reemitir continua valendo; o que parou foi o job.
	 */
	emissaoFalhaPermanente: boolean;
	/** Quando a última tentativa aconteceu (ISO); `null` se nunca tentou. */
	emissaoUltimaTentativaEm: string | null;
	/** Onde está o lançamento do estoque na Tiny (#2141). */
	statusEstoqueTiny: StatusEstoqueTinyDevolucao;
	/** Etapa, SKU e onde o item ficou; `null` quando não houve falha. */
	estoqueTinyErro: string | null;
	/** True se algum item ficou em tratativa (não-perfeito) — "com ocorrência". */
	temOcorrencia: boolean;
	totalItens: number;
	/**
	 * Valor da devolução em reais; `null` quando nenhuma fonte resolve.
	 *
	 * Vem da NF de devolução; não havendo, da nota de venda de origem; não
	 * havendo nota, do pedido. A devolução do canal costuma chegar sem nenhuma
	 * das três e fica nula até a nota entrar.
	 */
	valor: number | null;
	/** Data em que a devolução foi gerada (ISO). */
	dataCriacao: string;
	/** Data em que a conferência foi concluída (ISO); null se ainda não foi. */
	dataConclusao: string | null;
	recebidaEm: string | null;

	// ── eixo do canal ───────────────────────────────────────────────────
	// Tudo `null` em devolução nascida no WMS. Os valores vêm CRUS do
	// marketplace, sem tradução: estado novo do canal aparece na tela sem
	// deploy, e o operador lê o mesmo termo que veria no painel dele.

	/** `mercadolivre` | `amazon` | … ; null = devolução nascida no WMS. */
	canal: string | null;
	/** Status cru do canal — inclui disputa e mediação. */
	statusExterno: string | null;
	/**
	 * Motivo **cru** do canal (`PDD9939`…). Suporte apenas — **não vai à tela**.
	 *
	 * Medido no banco: 89% das linhas trazem o mesmo código, e os quatro valores
	 * existentes são igualmente opacos. Não há rota de tradução: o
	 * `returns/reasons` devolve `SRF…`, que é o vocabulário da nossa recusa, não
	 * o motivo do comprador.
	 */
	motivoExterno: string | null;
	/**
	 * O motivo em português (`/detail.problem`) — é este que a tela mostra.
	 *
	 * `null` para o que fica no CD do canal, e isso é esperado: não buscamos o
	 * `/detail` dessas, e o motivo delas não nos diz respeito. Coluna vazia diz
	 * "não sei"; o código cru dizia o mesmo ocupando espaço.
	 */
	motivoTexto: string | null;
	/** **Cru** do canal: `seller_address` | `warehouse` no ML. Exibição apenas. */
	destinoExterno: string | null;
	/**
	 * Destino normalizado — é por ele que se filtra e se decide.
	 *
	 * `null` = devolução nascida no WMS. O `destinoExterno` fica ao lado só para
	 * a tela poder mostrar a palavra do canal.
	 */
	destino: DestinoDevolucao | null;
	/**
	 * Status do envio que vem para NÓS — **não** o do return (`statusExterno`).
	 *
	 * É o único que separa "vem chegando" de "o comprador nem postou". `null`
	 * quando não há perna para a nossa doca.
	 */
	statusEnvioExterno: string | null;
	/** `claim.status` + `/` + `claim.stage`, cru. Ex.: `opened/dispute`. */
	situacaoExterna: string | null;
	/** Texto legível do canal, já em português (`/detail.title`). */
	situacaoTexto: string | null;
	/**
	 * Prazo para resolver a reclamação (ISO, `/detail.due_date`).
	 *
	 * Nulo para o que fica no CD do canal (o `/detail` só é consultado para o
	 * que vem à nossa doca). Pode vir **sem** `prazoPara`: prazo da claim sem
	 * responsável definido.
	 */
	prazoEm: string | null;
	/**
	 * De **quem** é o prazo, cru: `respondent` (nós) ou `complainant` (comprador).
	 *
	 * Nulo quando o canal não diz. Para exibir como atraso **nosso**, exigir
	 * `respondent`: com `complainant` a mesma data é do comprador.
	 */
	prazoPara: string | null;
	/**
	 * Previsão de chegada no nosso endereço (ISO), do ML. `null` para o que fica
	 * no CD do canal. Fixada na criação da etiqueta: não anda se o comprador
	 * atrasa a postagem — "passou da previsão" nem sempre é atraso de transporte.
	 */
	previsaoEntregaEm: string | null;
	/** Pedido no canal — o que a tela mostra quando o pedido local falta. */
	pedidoExterno: string | null;
	/** Envio bipável; null enquanto não houver perna para a nossa doca. */
	codigoEnvio: string | null;
	codigoRastreio: string | null;
	devolucaoExternaId: string | null;
	referenciaExterna: string | null;
	/** Quando o canal confirmou a entrega no nosso endereço (ISO). */
	entregueEm: string | null;
	/** Quantas linhas o canal prometeu. 0 quando não há eixo de canal. */
	itensPrevistos: number;
	/** Dias desde a entrega — o relógio da cobrança. Para em EM_CONFERENCIA. */
	diasDesdeEntrega: number | null;
	/**
	 * Onde a devolução está no fluxo do canal (Etapa 2.8), traduzido pelo
	 * backend. `null` para devolução nascida no WMS.
	 */
	fluxoCanal: FluxoCanal | null;
}

/** Etapa 2.8 — a regra mora no backend; aqui só o formato. */
export type StatusFluxoCanal =
	| "CRIADA"
	| "EM_TRANSITO"
	| "ENTREGUE"
	| "EM_DISPUTA"
	| "ENCERRADA";

export interface FluxoCanal {
	/** `null` quando o canal disse algo que a regra não conhece: mostrar o cru. */
	status: StatusFluxoCanal | null;
	/** O claim fechou, mas o pacote ainda não chegou nem vai deixar de chegar. */
	reclamacaoEncerrada: boolean;
	/** Encerrou depois de disputa (`closed/dispute` ou `closed/recontact`). */
	teveDisputa: boolean;
}

// ---------- CONFERÊNCIA (read model) ----------

export interface ConferenciaItem {
	itemId: number;
	produtoId: number;
	quantidade: number;
	qualidadeId?: number;
	motivoId?: number;
	observacao?: string;
	status: StatusItemDevolucao;
	descricao: string;
	ean: string | null;
	sku: string | null;
	valorUnitario: number;
	quantidadeNota: number;
}

export interface PedidoConferencia {
	pedidoId: number;
	numPedido: string;
	numPedidoEcommerce: string | null;
	numPedidoTiny: string | null;
	canalVenda: string | null;
	clienteNome: string | null;
	data: string | null;
	valor: string | null;
	situacao: string | null;
}

export interface NotaHeader {
	notaFiscalId: number;
	numeroNota: string;
	chaveAcesso: string;
}

export interface Conferencia {
	id: number;
	status: StatusDevolucao;
	origem: OrigemDevolucao;
	notaFiscalId?: number;
	pedidoId?: number;
	notaVendaId?: number;
	/** Nota de venda de origem (origem pedido). */
	notaVenda: NotaHeader | null;
	/** NF de devolução existente (origem nota). */
	notaDevolucao: NotaHeader | null;
	pedido: PedidoConferencia | null;
	/** Há rascunho no Tiny mas a NF ainda não foi vinculada (emissão assíncrona). */
	podeVincularNota: boolean;
	itens: ConferenciaItem[];
}

export interface ConferirItemPayload {
	itemId?: number;
	produtoId: number;
	quantidade: number;
	qualidadeId: number;
	motivoId: number;
	observacao?: string;
}

// ---------- QUALIDADES ----------

export const listarQualidades = async (
	incluirInativos = false,
): Promise<QualidadeProduto[]> => {
	const response = await api.get<QualidadeProduto[]>("/devolucao/qualidades", {
		params: incluirInativos ? { incluirInativos: true } : undefined,
	});
	return response.data;
};

export const cadastrarQualidade = async (
	dto: CadastrarQualidadeDto,
): Promise<QualidadeProduto> => {
	const response = await api.post<QualidadeProduto>(
		"/devolucao/qualidades",
		dto,
	);
	return response.data;
};

/**
 * Edita o cadastro da qualidade (nome, destino, "Gera NF", tratativa).
 *
 * Manda o formulario INTEIRO, nao campo solto: `entraNotaDevolucao: false` e
 * ausente seriam indistinguiveis, e esse Sim/Nao e justamente o que a edicao
 * existe para corrigir.
 *
 * **Vale para tras**: o destino e lido ao vivo por quem classifica, entao
 * trocar TRATATIVA por ESTOQUE muda o que o sistema diz sobre devolucao ja
 * conferida.
 */
export const editarQualidade = async (
	id: number,
	dto: CadastrarQualidadeDto,
): Promise<QualidadeProduto> => {
	const response = await api.patch<QualidadeProduto>(
		`/devolucao/qualidades/${id}`,
		dto,
	);
	return response.data;
};

export const desativarQualidade = async (
	id: number,
): Promise<QualidadeProduto> => {
	const response = await api.patch<QualidadeProduto>(
		`/devolucao/qualidades/${id}/desativar`,
	);
	return response.data;
};

export const reativarQualidade = async (
	id: number,
): Promise<QualidadeProduto> => {
	const response = await api.patch<QualidadeProduto>(
		`/devolucao/qualidades/${id}/reativar`,
	);
	return response.data;
};

// ---------- MOTIVOS ----------

export const listarMotivos = async (
	incluirInativos = false,
): Promise<MotivoDevolucao[]> => {
	const response = await api.get<MotivoDevolucao[]>("/devolucao/motivos", {
		params: incluirInativos ? { incluirInativos: true } : undefined,
	});
	return response.data;
};

export const cadastrarMotivo = async (
	dto: CadastrarMotivoDto,
): Promise<MotivoDevolucao> => {
	const response = await api.post<MotivoDevolucao>("/devolucao/motivos", dto);
	return response.data;
};

export const desativarMotivo = async (
	id: number,
): Promise<MotivoDevolucao> => {
	const response = await api.patch<MotivoDevolucao>(
		`/devolucao/motivos/${id}/desativar`,
	);
	return response.data;
};

export const reativarMotivo = async (
	id: number,
): Promise<MotivoDevolucao> => {
	const response = await api.patch<MotivoDevolucao>(
		`/devolucao/motivos/${id}/reativar`,
	);
	return response.data;
};

// ---------- DEVOLUÇÕES ----------

export const conferirNota = async (
	payload: ConferirNotaPayload,
): Promise<DevolucaoResumo> => {
	const response = await api.post<DevolucaoResumo>(
		"/devolucao/conferir-nota",
		payload,
	);
	return response.data;
};

/** Qual aba está perguntando (#1965). */
export type OrigemVendaDevolucao = "PEDIDO" | "FULL";

export interface ListarDevolucoesParams {
	/**
	 * A aba: `PEDIDO` ou `FULL` (#1965).
	 *
	 * Ausente não filtra — é o que mantém intacto quem chamava antes. A aba
	 * ativa manda a sua, e é só isso que separa as duas listas.
	 */
	origem?: OrigemVendaDevolucao;
	/** Status aceitos; lista vazia/ausente = todos. */
	status?: StatusDevolucao[];
	/**
	 * Canais aceitos; lista vazia/ausente = todos. Cobre a devolução de
	 * marketplace sem pedido: o canal dela vem da conta (`ml_seller_id`).
	 */
	canalVenda?: string[];
	numPedido?: string;
	nota?: string;
	cliente?: string;
	/** Período por data de geração — YYYY-MM-DD. */
	dataInicio?: string;
	dataFim?: string;
	entregueInicio?: string;
	entregueFim?: string;
	recebidaInicio?: string;
	recebidaFim?: string;
	conferidaInicio?: string;
	conferidaFim?: string;
	/**
	 * Separa a fila acionável do acompanhamento.
	 *
	 * `false` — só o que vem (ou pode vir) para a nossa doca, **incluindo**
	 * devolução nascida no WMS, que não tem eixo de canal.
	 * `true` — só o que está parado no CD do marketplace.
	 * Ausente — tudo.
	 *
	 * Existe porque 72% das devoluções que o canal registra (medido em 2.000
	 * reais) nunca chegam aqui: sem a separação, a fila nasceria com três
	 * previsões fantasma para cada uma acionável.
	 */
	noCdDoCanal?: boolean;
	/** `true` traz só devolução com emissão pendente ou em erro (#1896). */
	emissaoPendente?: boolean;
	offset?: number;
	limit?: number;
}

export interface ListarDevolucoesResponse {
	results: DevolucaoListaItem[];
	total: number;
}

export const listarDevolucoes = async (
	params: ListarDevolucoesParams = {},
): Promise<ListarDevolucoesResponse> => {
	const response = await api.get<ListarDevolucoesResponse>("/devolucao", {
		params,
		// `indexes: null` serializa lista como `status=A&status=B` (formato
		// repetido). O default do axios (`status[]=A`) NÃO funciona: o backend
		// roda Express 5, cujo parser de query padrão não interpreta colchetes e
		// entregaria a chave literal `status[]`, ignorando o filtro (#1450).
		paramsSerializer: { indexes: null },
	});
	return response.data;
};

/** Contagem por status do WMS, como o resumo devolve. */
export type ContagemPorStatus = Record<StatusDevolucao, number>;

export interface ResumoCanalApi {
	/** `null` quando o canal não foi resolvido. */
	canal: string | null;
	/** Devoluções + notas de devolução ainda sem devolução aberta. */
	total: number;
	valor: number;
	porStatus: ContagemPorStatus;
	comOcorrencia: number;
	notasAGerar: number;
}

export interface ResumoDevolucoesApi {
	total: number;
	valor: number;
	porStatus: ContagemPorStatus;
	comOcorrencia: number;
	notasAGerar: number;
	porCanal: ResumoCanalApi[];
}

/**
 * Os cards e as contagens das abas, numa query só no backend.
 *
 * Aceita os mesmos filtros da listagem **menos o status** — o resumo cruza os
 * status, e as abas precisam da contagem de recortes que não são o atual.
 */
export const buscarResumoDevolucoes = async (
	params: Omit<ListarDevolucoesParams, "status" | "offset" | "limit"> = {},
): Promise<ResumoDevolucoesApi> => {
	const response = await api.get<ResumoDevolucoesApi>("/devolucao/resumo", {
		params,
		// Mesmo motivo da listagem: Express 5 não interpreta colchetes.
		paramsSerializer: { indexes: null },
	});
	return response.data;
};

export const buscarDevolucao = async (
	id: number,
): Promise<DevolucaoDetalhe> => {
	const response = await api.get<DevolucaoDetalhe>(`/devolucao/${id}`);
	return response.data;
};

/** Um produto que o canal diz estar voltando. */
export interface ItemPrevistoDetalhe {
	pedidoExterno: string | null;
	/** O anúncio no canal (`MLB…`) — não é SKU. */
	itemExterno: string | null;
	variacao: string | null;
	/** `total` | `partial` | `incomplete`, cru. */
	contexto: string | null;
	/** A quantidade que VOLTA, não a vendida. */
	quantidade: number;
	/** Produto do WMS, quando o SKU ou o EAN casou com o cadastro. */
	produtoId: number | null;
	/** SKU do vendedor, vindo da venda no canal. `null` se o canal não respondeu. */
	sku: string | null;
	/** EAN, da nota de venda no canal. */
	ean: string | null;
	/** Descrição do produto no WMS. */
	descricao: string | null;
}

/** Prévia só de leitura da devolução do canal (`GET /devolucao/:id/canal`). */
export interface DevolucaoCanalDetalhe {
	devolucaoId: number;
	status: StatusDevolucao;
	canal: string;
	conta: string;
	devolucaoExternaId: string;
	referenciaExterna: string | null;
	pedidoExterno: string | null;
	destino: DestinoDevolucao | null;
	statusExterno: string | null;
	statusEnvioExterno: string | null;
	situacaoExterna: string | null;
	situacaoTexto: string | null;
	motivoTexto: string | null;
	codigoEnvio: string | null;
	codigoRastreio: string | null;
	entregueEm: string | null;
	previsaoEntregaEm: string | null;
	itensPrevistos: ItemPrevistoDetalhe[];
}

export const buscarDevolucaoCanal = async (
	id: number,
): Promise<DevolucaoCanalDetalhe> => {
	const response = await api.get<DevolucaoCanalDetalhe>(
		`/devolucao/${id}/canal`,
	);
	return response.data;
};

// ---------- ABERTURA + CONFERÊNCIA ----------

/** Abre (ou retorna a já aberta) a conferência de uma NF de devolução. */
export const abrirPorNota = async (
	notaFiscalId: number,
): Promise<DevolucaoResumo> => {
	const response = await api.post<DevolucaoResumo>("/devolucao/abrir-por-nota", {
		notaFiscalId,
	});
	return response.data;
};

/** Abre (ou retorna a já aberta) a conferência a partir de um pedido. */
export const abrirPorPedido = async (
	pedidoId: number,
): Promise<DevolucaoResumo> => {
	const response = await api.post<DevolucaoResumo>(
		"/devolucao/abrir-por-pedido",
		{ pedidoId },
	);
	return response.data;
};

/**
 * Abre a conferência bipando a etiqueta da embalagem do Mercado Livre.
 * Resolve o shipment_id (via Gestão) → NF de devolução já importada.
 */
/**
 * Por que o bipe não montou a conferência. A mensagem é para o operador; o
 * código permite tratar casos específicos no futuro.
 */
export interface PendenciaMontagem {
	codigo:
		| "PRODUTO_NAO_CADASTRADO"
		| "SEM_ITENS_NO_CANAL"
		| "CANAL_INDISPONIVEL"
		| "NF_DEVOLUCAO_INDISPONIVEL"
		| "VENDA_FULL"
		| "SEM_NOTA_DE_VENDA"
		| "NOTA_DE_VENDA_NAO_ENCONTRADA"
		| "FALHA_AO_ABRIR";
	mensagem: string;
}

/** Resposta do bipe: a devolução e, quando não montou, o motivo. */
export interface ResultadoBipeDevolucao extends DevolucaoResumo {
	pendenciaMontagem?: PendenciaMontagem | null;
	/**
	 * Recado que não é pendência (#2022): o volume de retirada do Full saiu com
	 * mais de uma NF e a conferência abriu pela primeira.
	 */
	aviso?: string | null;
}

export const resolverEtiqueta = async (
	shipmentId: string,
): Promise<ResultadoBipeDevolucao> => {
	const response = await api.post<ResultadoBipeDevolucao>(
		"/devolucao/resolver-etiqueta",
		// #2022: `codigo` é o nome novo; `shipmentId` vai junto porque o
		// backend ainda o aceita e os dois lados não sobem no mesmo instante.
		{ codigo: shipmentId, shipmentId },
	);
	return response.data;
};

/** Carrega o read model da conferência (devolução + nota de origem + itens). */
export const getConferencia = async (id: number): Promise<Conferencia> => {
	const response = await api.get<Conferencia>(`/devolucao/${id}/conferencia`);
	return response.data;
};

/**
 * Conclui a conferência em lote (aceita divisão de linha). Os dados fiscais da
 * NF de devolução (origem pedido) são fixos no backend — não são enviados.
 */
export const concluirConferencia = async (
	id: number,
	itens: ConferirItemPayload[],
	localizacaoId?: number,
): Promise<DevolucaoResumo> => {
	const response = await api.put<DevolucaoResumo>(`/devolucao/${id}/conferir`, {
		itens,
		localizacaoId,
	});
	return response.data;
};

// ---------- REVISÃO NO CANAL (Mercado Livre) ----------

export type ResultadoRevisaoCanal = "OK" | "FAIL";
export type StatusRevisaoCanal =
	| "SUGERIDA"
	| "ENVIADA"
	| "FALHOU"
	| "INDISPONIVEL";

/** Motivo aceito pelo canal para reportar problema (`SRF…` no ML). */
export interface MotivoRevisaoCanal {
	id: string;
	descricao: string;
	/** O canal exige foto como evidência (SRF2 e SRF4 no ML). */
	exigeEvidencia: boolean;
}

/** Para quem vai a mensagem: o canal decide pela etapa da reclamação. */
export type DestinatarioMensagemCanal = "comprador" | "mediador";

/**
 * Reembolso, mediação e mensagem — o que o canal oferece **agora** (Etapa 4.8).
 *
 * Nenhuma depende de concluir a conferência: são conversas com o Mercado Livre,
 * não com o WMS.
 */
export interface AcoesCanalDisponiveis {
	reembolsar: {
		disponivel: boolean;
		motivoIndisponivel: string | null;
		/** Total do pedido no canal — só para a confirmação. */
		valor: number | null;
	};
	abrirMediacao: { disponivel: boolean };
	enviarMensagem: {
		disponivel: boolean;
		destinatario: DestinatarioMensagemCanal | null;
	};
	/** `status_money` cru: é ele que explica o reembolso indisponível. */
	statusDinheiro: string | null;
}

/** `GET /devolucao/:id/revisao-canal` — só leitura. */
export interface OpcoesRevisaoCanal {
	disponivel: boolean;
	motivoIndisponivel: string | null;
	jaEnviada: boolean;
	resultadoEnviado: ResultadoRevisaoCanal | null;
	podeAprovar: boolean;
	podeReprovar: boolean;
	motivos: MotivoRevisaoCanal[];
	motivoPadrao: string;
	/**
	 * Por que o **comprador** abriu a devolução — a metade que faltava para
	 * decidir o veredito. `texto` é a frase do ML; `codigo` o `reason_id`
	 * (`PDD9939`). `null` quando o canal não informou.
	 */
	motivoComprador: { texto: string; codigo: string | null } | null;
	/**
	 * Por que a CONFERÊNCIA não autoriza aprovar, ou `null` (Etapa 4.11).
	 *
	 * Diferente de `podeAprovar`, que é o que o canal aceita: o ML libera a
	 * revisão assim que o comprador posta, com o pacote ainda em trânsito.
	 */
	travaAprovar: string | null;
	/** O mesmo para reportar problema. Aceita divergência; exige conferência. */
	travaReportar: string | null;
	/**
	 * O motivo que a trava aceita SEM conferência, quando o canal o oferece.
	 *
	 * Hoje o `SRF7` ("ainda não chegou"). Vem do backend de propósito: a exceção
	 * é de lá, e a tela não deve ter a constante.
	 */
	motivoSemConferencia: string | null;
	acoes: AcoesCanalDisponiveis;
}

/** O que o operador decide no envio. Vazio = aprovar, com os padrões do WMS. */
export interface DecisaoRevisaoCanal {
	/**
	 * O veredito. Ausente vale `OK`: aprovar é o caso comum, e reprovar é
	 * contestar a devolução — precisa ser escolha deliberada.
	 */
	resultado?: ResultadoRevisaoCanal;
	motivoExterno?: string;
	mensagem?: string;
}

export interface ResultadoEnvioRevisao {
	status: StatusRevisaoCanal;
	motivo?: string;
}

export const consultarRevisaoCanal = async (
	id: number,
): Promise<OpcoesRevisaoCanal> => {
	const response = await api.get<OpcoesRevisaoCanal>(
		`/devolucao/${id}/revisao-canal`,
	);
	return response.data;
};

export const enviarRevisaoCanal = async (
	id: number,
	decisao: DecisaoRevisaoCanal,
): Promise<ResultadoEnvioRevisao> => {
	const response = await api.post<ResultadoEnvioRevisao>(
		`/devolucao/${id}/revisar-canal`,
		decisao,
	);
	return response.data;
};

// ---------- OUTRAS AÇÕES NO CANAL (Etapa 4.8) ----------

export type StatusAcaoCanal = "ENVIADA" | "FALHOU" | "INDISPONIVEL";

export interface ResultadoAcaoCanal {
	status: StatusAcaoCanal;
	/** Por que não deu. É o texto que vai ao operador. */
	motivo?: string;
}

/**
 * Devolve o dinheiro ao comprador. **Total, e encerra a reclamação.**
 *
 * Sem corpo de propósito: quanto devolver é do canal. O backend confere na hora
 * se a ação está disponível e se o comprador já não foi reembolsado.
 */
export const reembolsarNoCanal = async (
	id: number,
): Promise<ResultadoAcaoCanal> => {
	const response = await api.post<ResultadoAcaoCanal>(
		`/devolucao/${id}/reembolsar-canal`,
	);
	return response.data;
};

/** Abre mediação. Sem volta: some a conversa direta com o comprador. */
export const abrirMediacaoNoCanal = async (
	id: number,
): Promise<ResultadoAcaoCanal> => {
	const response = await api.post<ResultadoAcaoCanal>(
		`/devolucao/${id}/mediacao-canal`,
	);
	return response.data;
};

/**
 * Mensagem na reclamação. O destinatário **não vai daqui**: quem decide é o
 * canal, pela etapa em que a reclamação está.
 */
export const enviarMensagemNoCanal = async (
	id: number,
	mensagem: string,
	anexarFotos = false,
): Promise<ResultadoAcaoCanal> => {
	const response = await api.post<ResultadoAcaoCanal>(
		`/devolucao/${id}/mensagem-canal`,
		{ mensagem, anexarFotos },
	);
	return response.data;
};

// ---------- FOTOS DE PROVA (por produto da devolução) ----------

export interface FotoItemDevolucao {
	/** Object key no storage — fonte da verdade. */
	objectKey: string;
	/** URL presigned (on-demand) da imagem. */
	url: string;
	/** URL presigned (on-demand) da miniatura. */
	thumbUrl: string;
}

/** Lista as fotos de prova de um produto numa devolução. */
export const listarFotosItem = async (
	devolucaoId: number,
	produtoId: number,
): Promise<FotoItemDevolucao[]> => {
	const response = await api.get<FotoItemDevolucao[]>(
		`/devolucao/${devolucaoId}/produtos/${produtoId}/fotos`,
	);
	return response.data;
};

/** Envia uma ou mais fotos (multipart) — o servidor converte para WebP + thumb. */
export const enviarFotosItem = async (
	devolucaoId: number,
	produtoId: number,
	arquivos: Blob[],
): Promise<FotoItemDevolucao[]> => {
	const formData = new FormData();
	arquivos.forEach((blob, i) => formData.append("arquivos", blob, `foto-${i}.jpg`));
	const response = await api.post<FotoItemDevolucao[]>(
		`/devolucao/${devolucaoId}/produtos/${produtoId}/fotos`,
		formData,
	);
	return response.data;
};

/** Remove uma foto (original + thumbnail) pelo object key. */
export const removerFotoItem = async (
	devolucaoId: number,
	produtoId: number,
	objectKey: string,
): Promise<void> => {
	await api.delete(`/devolucao/${devolucaoId}/produtos/${produtoId}/fotos`, {
		params: { objectKey },
	});
};

// ---------- LOCALIZAÇÕES (armazém "Devolução") ----------

export interface LocalizacaoDevolucao {
	localizacao_id: number;
	nome: string;
	ean: string;
	status: string;
}

/** Localizações do armazém "Devolução" (destino dos itens em Perfeito Estado). */
export const listarLocalizacoesDevolucao = async (): Promise<
	LocalizacaoDevolucao[]
> => {
	const response = await api.get<LocalizacaoDevolucao[]>(
		"/localizacao/devolucao",
	);
	return response.data;
};

/**
 * Vincula a NF de devolução (origem pedido) quando a emissão foi assíncrona —
 * tenta importar/vincular pelo rascunho já criado no Tiny.
 */
export const vincularNotaDevolucao = async (
	id: number,
): Promise<DevolucaoResumo> => {
	const response = await api.post<DevolucaoResumo>(
		`/devolucao/${id}/vincular-nota`,
	);
	return response.data;
};

/**
 * Retenta a emissão da NF de devolução sem refazer a conferência (#1896).
 *
 * Diferente de `vincularNotaDevolucao`: aquele só resolve quando o rascunho já
 * existe na Tiny. Este cobre também a devolução em que a inclusão do rascunho
 * é que falhou — o caso do `access_token_v2` ausente.
 */
export const reemitirNotaDevolucao = async (
	id: number,
): Promise<DevolucaoResumo> => {
	const response = await api.post<DevolucaoResumo>(
		`/devolucao/${id}/reemitir-nota`,
	);
	return response.data;
};

/**
 * Retenta o lançamento do estoque na Tiny que parou no meio (#2141). Refaz só o
 * que falta — a NF já lançada e as transferências feitas não se repetem.
 */
export const retentarEstoqueTinyDevolucao = async (
	id: number,
): Promise<
	DevolucaoResumo & {
		statusEstoqueTiny: StatusEstoqueTinyDevolucao;
		estoqueTinyErro?: string;
	}
> => {
	const response = await api.post<
		DevolucaoResumo & {
			statusEstoqueTiny: StatusEstoqueTinyDevolucao;
			estoqueTinyErro?: string;
		}
	>(
		`/devolucao/${id}/retentar-estoque-tiny`,
	);
	return response.data;
};

export const vincularNotaExistenteDevolucao = async (
	id: number,
	payload: { chaveAcesso: string; estoqueJaLancado: boolean },
): Promise<DevolucaoResumo> => {
	const response = await api.post<DevolucaoResumo>(
		`/devolucao/${id}/vincular-nota-existente`,
		payload,
	);
	return response.data;
};

// ---------- OCORRÊNCIAS DE DEVOLUÇÃO ----------

export type TipoOcorrenciaDevolucao =
	| "DEVOLUCAO_FORNECEDOR"
	| "DEVOLUCAO_CLIENTE";

export type StatusOcorrenciaDevolucao =
	| "PENDENTE_TRATATIVA"
	| "EM_TRATATIVA"
	| "AGUARDANDO_COLETA"
	| "FINALIZADO";

export interface TratativaDevolucaoApi {
	id?: number;
	acao: string;
	detalhe?: string;
	usuarioId?: number;
	/** Nome de quem executou a ação (já resolvido pelo backend). */
	usuarioNome?: string;
	dataHora: string;
}

export interface OcorrenciaDevolucao {
	id: number;
	tipo: TipoOcorrenciaDevolucao;
	status: StatusOcorrenciaDevolucao;
	devolucaoId?: number;
	notaFiscalId?: number;
	notaFiscalNumero?: string;
	pedidoId?: number;
	pedidoNumero?: string;
	pedidoEcommerce?: string;
	canalVenda?: string;
	clienteNome?: string;
	clienteDocumento?: string;
	produtoId: number;
	produtoDescricao?: string;
	ean?: string;
	quantidade: number;
	motivoDevolucaoId?: number;
	motivo?: string;
	criadoPorUsuarioId?: number;
	assumidoPorUsuarioId?: number;
	/** Quem fez a análise (nome já resolvido pelo backend). */
	criadoPorNome?: string;
	/** Quem fez a tratativa (nome já resolvido pelo backend). */
	assumidoPorNome?: string;
	observacao?: string;
	notaReferenciaId?: number;
	notaReferenciaNumero?: string;
	fornecedorCnpj?: string;
	fornecedorNome?: string;
	devolucaoCfop?: string;
	devolucaoFinalidade?: string;
	numeroNotaDevolucao?: string;
	serieNotaDevolucao?: string;
	chaveAcessoDevolucao?: string;
	dataDevolucaoEmitida?: string;
	idNotaDevolucaoTiny?: number;
	linkAcessoDevolucao?: string;
	temXmlDevolucao?: boolean;
	dataCriacao: string;
	dataResolucao?: string;
	tratativas: TratativaDevolucaoApi[];
}

export interface ReferenciaNotaEntrada {
	notaFiscalId: number;
	numeroNota: string;
	serie: string;
	chaveAcesso: string;
	dataEmissao: string;
	fornecedorCnpj: string;
	fornecedorNome: string;
	fornecedorEmail?: string;
	ean?: string;
	ncm?: string;
	unidade?: string;
	valorUnitario?: number;
}

export interface EmitirNotaDevolucaoPayload {
	cfop?: string;
	finalidade?: string;
	observacao?: string;
}

export interface ListarOcorrenciasDevolucaoParams {
	status?: StatusOcorrenciaDevolucao;
	tipo?: TipoOcorrenciaDevolucao;
	/** Periodo pela data de criacao da ocorrencia (#2063), inclusivo. */
	dataInicio?: string;
	dataFim?: string;
	canalVenda?: string | string[];
	origem?: OrigemVendaDevolucao;
	qualidadeProdutoId?: number;
}

export const listarOcorrenciasDevolucao = async (
	params?: ListarOcorrenciasDevolucaoParams,
): Promise<OcorrenciaDevolucao[]> => {
	const response = await api.get<OcorrenciaDevolucao[]>(
		"/devolucao/ocorrencias",
		{ params, paramsSerializer: { indexes: null } },
	);
	return response.data;
};

/**
 * Quantas ocorrências ainda não foram finalizadas — alimenta o indicador do
 * botão "Ocorrências" (#1450). Endpoint dedicado de contagem: baixar a lista
 * inteira só para exibir um número não escala.
 */
export const contarOcorrenciasDevolucaoEmAberto = async (
	origem?: OrigemVendaDevolucao,
): Promise<number> => {
	const response = await api.get<{ emAberto: number }>(
		"/devolucao/ocorrencias/contagem",
		{ params: origem ? { origem } : undefined },
	);
	return response.data.emAberto;
};

export const buscarOcorrenciaDevolucao = async (
	id: number,
): Promise<OcorrenciaDevolucao> => {
	const response = await api.get<OcorrenciaDevolucao>(
		`/devolucao/ocorrencias/${id}`,
	);
	return response.data;
};

export const buscarReferenciaFornecedor = async (
	id: number,
): Promise<ReferenciaNotaEntrada | null> => {
	const response = await api.get<{ referencia: ReferenciaNotaEntrada | null }>(
		`/devolucao/ocorrencias/${id}/referencia-fornecedor`,
	);
	return response.data.referencia;
};

export const emitirNotaDevolucao = async (
	id: number,
	payload: EmitirNotaDevolucaoPayload,
): Promise<OcorrenciaDevolucao> => {
	const response = await api.patch<OcorrenciaDevolucao>(
		`/devolucao/ocorrencias/${id}/emitir-nota-devolucao`,
		payload,
	);
	return response.data;
};

export const comunicarFornecedorDevolucao = async (
	id: number,
	detalhe?: string,
): Promise<OcorrenciaDevolucao> => {
	const response = await api.patch<OcorrenciaDevolucao>(
		`/devolucao/ocorrencias/${id}/comunicar-fornecedor`,
		{ detalhe },
	);
	return response.data;
};

export const marcarAguardandoColetaDevolucao = async (
	id: number,
): Promise<OcorrenciaDevolucao> => {
	const response = await api.patch<OcorrenciaDevolucao>(
		`/devolucao/ocorrencias/${id}/aguardando-coleta`,
	);
	return response.data;
};

export const finalizarOcorrenciaDevolucao = async (
	id: number,
): Promise<OcorrenciaDevolucao> => {
	const response = await api.patch<OcorrenciaDevolucao>(
		`/devolucao/ocorrencias/${id}/finalizar`,
	);
	return response.data;
};

export const gerarRascunhoTinyDevolucao = async (
	id: number,
	payload: EmitirNotaDevolucaoPayload,
): Promise<OcorrenciaDevolucao> => {
	const response = await api.patch<OcorrenciaDevolucao>(
		`/devolucao/ocorrencias/${id}/rascunho-tiny`,
		payload,
	);
	return response.data;
};

export const emitirTinyDevolucao = async (
	id: number,
): Promise<OcorrenciaDevolucao> => {
	const response = await api.patch<OcorrenciaDevolucao>(
		`/devolucao/ocorrencias/${id}/emitir-tiny`,
	);
	return response.data;
};

export const baixarXmlDevolucao = async (
	id: number,
): Promise<{ xml: string; chaveAcesso?: string; numero?: string }> => {
	const response = await api.get<{
		xml: string;
		chaveAcesso?: string;
		numero?: string;
	}>(`/devolucao/ocorrencias/${id}/xml-devolucao`);
	return response.data;
};

// Gera a PRÉVIA (PDF) da nota de devolução ao fornecedor (conteúdo antes de emitir).
export const baixarPreviaDevolucao = async (
	id: number,
	cfop: string,
	finalidade: string,
): Promise<Blob> => {
	const response = await api.get(`/devolucao/ocorrencias/${id}/previa-pdf`, {
		params: { cfop, finalidade },
		responseType: "blob",
	});
	return response.data as Blob;
};

// Baixa a DANFE (PDF) da nota de devolução, gerada no backend a partir do XML.
export const baixarDanfeDevolucao = async (id: number): Promise<Blob> => {
	const response = await api.get(`/devolucao/ocorrencias/${id}/danfe-pdf`, {
		responseType: "blob",
	});
	return response.data as Blob;
};

// Lista os nomes dos canais de venda reais do sistema (para filtros).
export const listarCanaisVenda = async (): Promise<string[]> => {
	const response = await api.get<{ canal_venda: string }[]>(
		"/pedido/filtros/canais-venda",
	);
	return response.data
		.map((c) => c.canal_venda)
		.filter((nome): nome is string => Boolean(nome));
};

// ---------- PENDÊNCIA DA MESA DE SEPARAÇÃO (#1768) ----------

export interface MesaSeparacaoResumo {
	id: number;
	nome: string;
	ean: string;
	ativo: boolean;
}

export interface PendenciaMesaItemPayload {
	produtoId: number;
	quantidade: number;
	qualidadeId: number;
	motivoId: number;
	observacao?: string;
}

export interface AbrirPendenciaMesaPayload {
	/** Mesa escolhida no select. Alternativa a `mesaEan`. */
	mesaSeparacaoId?: number;
	/** EAN da etiqueta da mesa, quando o operador bipa em vez de escolher. */
	mesaEan?: string;
	/** Localização do armazém "Devolução" que recebe os itens que voltam ao estoque. */
	localizacaoId?: number;
	itens: PendenciaMesaItemPayload[];
}

export interface SobraPorProduto {
	produtoId: number;
	sku: string | null;
	descricao: string | null;
	ean: string | null;
	quantidade: number;
	linhas: number;
	quantidadeEstoque: number;
	quantidadeTratativa: number;
}

export interface SobraPorMesa {
	mesaSeparacaoId: number | null;
	mesaNome: string | null;
	quantidade: number;
	linhas: number;
	produtos: number;
	quantidadeEstoque: number;
	quantidadeTratativa: number;
}

export interface RelatorioSobraMesa {
	porProduto: SobraPorProduto[];
	porMesa: SobraPorMesa[];
	totais: {
		quantidade: number;
		linhas: number;
		quantidadeEstoque: number;
		quantidadeTratativa: number;
	};
}

export interface FiltroRelatorioMesa {
	dataInicio?: string;
	dataFim?: string;
	mesaSeparacaoId?: number;
	motivoDevolucaoId?: number;
}

/**
 * Mesas para o select e para resolver o EAN bipado sem ida ao servidor.
 *
 * Por padrão só as ativas. `incluirInativas` existe para o filtro do relatório:
 * mesa desativada continua aparecendo no histórico, então precisa dar para
 * isolá-la.
 */
export const listarMesasSeparacao = async (
	incluirInativas = false,
): Promise<MesaSeparacaoResumo[]> => {
	const response = await api.get<MesaSeparacaoResumo[]>("/devolucao/mesas", {
		params: incluirInativas ? { incluirInativas: "true" } : undefined,
	});
	return response.data;
};

/** Registra a sobra contada na mesa. */
export const abrirPendenciaMesa = async (
	payload: AbrirPendenciaMesaPayload,
): Promise<DevolucaoResumo> => {
	const response = await api.post<DevolucaoResumo>("/devolucao/mesa", payload);
	return response.data;
};

/** Sobra da mesa por produto e por mesa no período. */
export const buscarRelatorioSobraMesa = async (
	filtro: FiltroRelatorioMesa = {},
): Promise<RelatorioSobraMesa> => {
	const response = await api.get<RelatorioSobraMesa>(
		"/devolucao/mesa/relatorio",
		{ params: filtro },
	);
	return response.data;
};

/* ------------------------------------------------------------------ */
/* Relatorio de devolucoes (#2063)                                      */
/* ------------------------------------------------------------------ */

/** Quantas devolucoes, pedidos e unidades em um recorte, e quanto valem. */
export interface ContagemDevolucoes {
	devolucoes: number;
	pedidos: number;
	unidades: number;
	/** Chegaram e ainda nao foram conferidas — nenhum item lancado. */
	semConferencia: number;
	valor: number;
}

export interface RelatorioCanal extends ContagemDevolucoes {
	canal: string | null;
}

/**
 * O recorte do Full.
 *
 * `pacotes` e `null` — nunca zero — quando a relacao de retirada ainda nao foi
 * populada. Zero se leria como "nao houve pacote"; a tela precisa distinguir.
 */
export interface RelatorioFull extends ContagemDevolucoes {
	pacotes: number | null;
	pacotesIndisponivelPorque: string | null;
}

export interface QualidadeLinha {
	qualidade: string | null;
	/** `true` volta ao estoque, `false` tratativa, `null` sem classificacao. */
	bomEstado: boolean | null;
	unidades: number;
}

export interface RelatorioQualidade {
	bons: number;
	naoBons: number;
	naoClassificados: number;
	porQualidade: QualidadeLinha[];
}

export interface ProdutoNaoBom {
	produtoId: number;
	sku: string | null;
	descricao: string | null;
	qualidade: string | null;
	unidades: number;
}

export interface RankingLinha {
	devolucaoId: number;
	pedido: string | null;
	cliente: string | null;
	canal: string | null;
	unidades: number;
	valor: number;
}

export interface OcorrenciaAgrupada {
	tipo: TipoOcorrenciaDevolucao;
	status: StatusOcorrenciaDevolucao;
	total: number;
}

/** O mesmo recorte na janela anterior, do mesmo tamanho. */
export interface ComparativoPeriodo {
	devolucoes: number;
	unidades: number;
	valor: number;
	/** Variacao % das devolucoes; `null` quando a janela anterior foi zero. */
	variacaoDevolucoes: number | null;
}

/**
 * Quanto do que foi vendido voltou.
 *
 * `percentual` e `null` — nunca 0 — quando nao ha denominador. E o caso do
 * Full, cujo pedido nao entra no WMS.
 */
export interface TaxaDevolucao {
	percentual: number | null;
	pedidosVendidos: number | null;
	meta: number | null;
	indisponivelPorque: string | null;
}

export type PeriodoPorRelatorio = "CONFERENCIA" | "ABERTURA";

export interface RelatorioDevolucoes {
	periodo: { inicio: string | null; fim: string | null; por: PeriodoPorRelatorio };
	/** De onde saiu o valor. `item`: quantidade devolvida x valor unitario. */
	valorFonte: "item";
	geral: ContagemDevolucoes;
	comparativo: ComparativoPeriodo | null;
	taxa: TaxaDevolucao;
	porCanal: RelatorioCanal[];
	/**
	 * Canais do recorte IGNORANDO o filtro de canal — e a lista do filtro.
	 *
	 * Vem do backend por isso mesmo: tirar de `porCanal` seria circular, porque
	 * ele ja vem filtrado e a lista encolheria para a opcao marcada.
	 */
	canaisDisponiveis: string[];
	full: RelatorioFull;
	qualidade: RelatorioQualidade;
	ranking: { porValor: RankingLinha[]; porUnidades: RankingLinha[] };
	naoBons: ProdutoNaoBom[];
	naoBonsTruncado: boolean;
	ocorrencias: {
		/** Ocorrencias CRIADAS no periodo. Nao ha historico de status. */
		criadas: number;
		/** Das criadas, as que seguem pendentes ou em tratativa. */
		aguardandoResolucao: number;
		porTipoStatus: OcorrenciaAgrupada[];
	};
}

export interface RelatorioDevolucoesParams {
	dataInicio?: string;
	dataFim?: string;
	canalVenda?: string[];
	/** Tipo: `PEDIDO` ou `FULL`. O canal filtra DENTRO do tipo. */
	origem?: OrigemVendaDevolucao;
	/** So devolucoes com ao menos um item desta qualidade. */
	qualidadeProdutoId?: number;
	periodoPor?: PeriodoPorRelatorio;
}

/**
 * O relatorio de devolucoes do periodo (#2063).
 *
 * Tudo ja vem agregado pelo backend. A tela nao soma nada: antes ela baixava
 * todas as ocorrencias e contava em `useMemo`, que e o que a issue corrige.
 */
export const obterRelatorioDevolucoes = async (
	params: RelatorioDevolucoesParams,
): Promise<RelatorioDevolucoes> => {
	const response = await api.get<RelatorioDevolucoes>("/devolucao/relatorio", {
		params,
		// `indexes: null` serializa a lista como `canalVenda=A&canalVenda=B`. O
		// default do axios (`canalVenda[]=A`) NAO funciona: o backend roda
		// Express 5, cujo parser de query ignora colchetes e entrega a chave
		// literal `canalVenda[]` — o filtro morria calado, mostrando todos os
		// canais com cara de filtrado. Mesma armadilha da #1450.
		paramsSerializer: { indexes: null },
	});
	return response.data;
};

/* ------------------------------------------------------------------ */
/* Meta de devolucao (#2063) — alvo do card "devolucoes / vendidos"     */
/* ------------------------------------------------------------------ */

/** `null` quando ninguem configurou: a tela nao mostra alvo nenhum. */
export const obterMetaDevolucao = async (): Promise<number | null> => {
	const response = await api.get<{ meta_devolucao_percentual: number | null }>(
		"/system-settings/meta-devolucao",
	);
	return response.data.meta_devolucao_percentual;
};

/** `null` apaga a meta — o card volta a dizer "sem alvo". */
export const salvarMetaDevolucao = async (
	percentual: number | null,
): Promise<number | null> => {
	const response = await api.put<{ meta_devolucao_percentual: number | null }>(
		"/system-settings/meta-devolucao",
		{ meta_devolucao_percentual: percentual },
	);
	return response.data.meta_devolucao_percentual;
};
